export enum Permission {
  LINKS_VIEW = 'links:view',
  LINKS_CREATE = 'links:create',
  LINKS_EDIT = 'links:edit',
  LINKS_DELETE = 'links:delete',
  LINKS_VIEW_ALL = 'links:view_all', // View all links (not just own)
  LINKS_EXPORT = 'links:export',

  GROUPS_VIEW = 'groups:view',
  GROUPS_CREATE = 'groups:create',
  GROUPS_EDIT = 'groups:edit',
  GROUPS_DELETE = 'groups:delete',

  USERS_VIEW = 'users:view',
  USERS_CREATE = 'users:create',
  USERS_EDIT = 'users:edit',
  USERS_DELETE = 'users:delete',
  USERS_MANAGE_ROLES = 'users:manage_roles',

  ATTRIBUTES_VIEW = 'attributes:view',
  ATTRIBUTES_CREATE = 'attributes:create',
  ATTRIBUTES_EDIT = 'attributes:edit',
  ATTRIBUTES_DELETE = 'attributes:delete',

  LINK_TEMPLATES_VIEW = 'link_templates:view',
  LINK_TEMPLATES_CREATE = 'link_templates:create',
  LINK_TEMPLATES_EDIT = 'link_templates:edit',
  LINK_TEMPLATES_DELETE = 'link_templates:delete',

  QR_TEMPLATES_VIEW = 'qr_templates:view',
  QR_TEMPLATES_CREATE = 'qr_templates:create',
  QR_TEMPLATES_EDIT = 'qr_templates:edit',
  QR_TEMPLATES_DELETE = 'qr_templates:delete',

  TRUSTED_DESTINATIONS_VIEW = 'trusted_destinations:view',
  TRUSTED_DESTINATIONS_CREATE = 'trusted_destinations:create',
  TRUSTED_DESTINATIONS_EDIT = 'trusted_destinations:edit',
  TRUSTED_DESTINATIONS_DELETE = 'trusted_destinations:delete',

  PENDING_LINKS_VIEW = 'pending_links:view',
  PENDING_LINKS_APPROVE = 'pending_links:approve',
  PENDING_LINKS_REJECT = 'pending_links:reject',

  IP_EXCLUSIONS_VIEW = 'ip_exclusions:view',
  IP_EXCLUSIONS_CREATE = 'ip_exclusions:create',
  IP_EXCLUSIONS_EDIT = 'ip_exclusions:edit',
  IP_EXCLUSIONS_DELETE = 'ip_exclusions:delete',

  TAGS_VIEW = 'tags:view',
  TAGS_CREATE = 'tags:create',
  TAGS_EDIT = 'tags:edit',
  TAGS_DELETE = 'tags:delete',

  POWER_PAGES_VIEW = 'power_pages:view',
  POWER_PAGES_CREATE = 'power_pages:create',
  POWER_PAGES_EDIT = 'power_pages:edit',
  POWER_PAGES_DELETE = 'power_pages:delete',

  ANALYTICS_VIEW = 'analytics:view',
  ANALYTICS_EXPORT = 'analytics:export',
  REPORTS_VIEW = 'reports:view',
  REPORTS_CREATE = 'reports:create',

  DOMAIN_VIEW = 'domain:view',
  DOMAIN_EDIT = 'domain:edit',
  DOMAIN_SETTINGS = 'domain:settings',

  ACCOUNT_VIEW = 'account:view',
  ACCOUNT_EDIT = 'account:edit',
  ACCOUNT_MANAGE_DOMAINS = 'account:manage_domains',

  SYSTEM_SETTINGS = 'system:settings',
  AUDIT_LOGS_VIEW = 'audit:view',

  E_LINKS_VIEW = 'e_links:view',
}

export enum PermissionGroup {
  LINKS = 'links',
  GROUPS = 'groups',
  USERS = 'users',
  ATTRIBUTES = 'attributes',
  TEMPLATES = 'templates',
  ADMIN = 'admin',
  ANALYTICS = 'analytics',
  ACCOUNT = 'account',
  SYSTEM = 'system',
}

export const PERMISSION_DISPLAY_NAMES: Record<Permission, string> = {
  [Permission.LINKS_VIEW]: 'View Links',
  [Permission.LINKS_CREATE]: 'Create Links',
  [Permission.LINKS_EDIT]: 'Edit Links',
  [Permission.LINKS_DELETE]: 'Delete Links',
  [Permission.LINKS_VIEW_ALL]: 'View All Links',
  [Permission.LINKS_EXPORT]: 'Export Links',

  [Permission.GROUPS_VIEW]: 'View Groups',
  [Permission.GROUPS_CREATE]: 'Create Groups',
  [Permission.GROUPS_EDIT]: 'Edit Groups',
  [Permission.GROUPS_DELETE]: 'Delete Groups',

  [Permission.USERS_VIEW]: 'View Users',
  [Permission.USERS_CREATE]: 'Create Users',
  [Permission.USERS_EDIT]: 'Edit Users',
  [Permission.USERS_DELETE]: 'Delete Users',
  [Permission.USERS_MANAGE_ROLES]: 'Manage User Roles',

  [Permission.ATTRIBUTES_VIEW]: 'View Attributes',
  [Permission.ATTRIBUTES_CREATE]: 'Create Attributes',
  [Permission.ATTRIBUTES_EDIT]: 'Edit Attributes',
  [Permission.ATTRIBUTES_DELETE]: 'Delete Attributes',

  [Permission.LINK_TEMPLATES_VIEW]: 'View Link Templates',
  [Permission.LINK_TEMPLATES_CREATE]: 'Create Link Templates',
  [Permission.LINK_TEMPLATES_EDIT]: 'Edit Link Templates',
  [Permission.LINK_TEMPLATES_DELETE]: 'Delete Link Templates',

  [Permission.QR_TEMPLATES_VIEW]: 'View QR Templates',
  [Permission.QR_TEMPLATES_CREATE]: 'Create QR Templates',
  [Permission.QR_TEMPLATES_EDIT]: 'Edit QR Templates',
  [Permission.QR_TEMPLATES_DELETE]: 'Delete QR Templates',

  [Permission.TRUSTED_DESTINATIONS_VIEW]: 'View Trusted Destinations',
  [Permission.TRUSTED_DESTINATIONS_CREATE]: 'Create Trusted Destinations',
  [Permission.TRUSTED_DESTINATIONS_EDIT]: 'Edit Trusted Destinations',
  [Permission.TRUSTED_DESTINATIONS_DELETE]: 'Delete Trusted Destinations',

  [Permission.PENDING_LINKS_VIEW]: 'View Pending Links',
  [Permission.PENDING_LINKS_APPROVE]: 'Approve Pending Links',
  [Permission.PENDING_LINKS_REJECT]: 'Reject Pending Links',

  [Permission.IP_EXCLUSIONS_VIEW]: 'View IP Exclusions',
  [Permission.IP_EXCLUSIONS_CREATE]: 'Create IP Exclusions',
  [Permission.IP_EXCLUSIONS_EDIT]: 'Edit IP Exclusions',
  [Permission.IP_EXCLUSIONS_DELETE]: 'Delete IP Exclusions',

  [Permission.TAGS_VIEW]: 'View Tags',
  [Permission.TAGS_CREATE]: 'Create Tags',
  [Permission.TAGS_EDIT]: 'Edit Tags',
  [Permission.TAGS_DELETE]: 'Delete Tags',

  [Permission.POWER_PAGES_VIEW]: 'View PowerPages',
  [Permission.POWER_PAGES_CREATE]: 'Create PowerPages',
  [Permission.POWER_PAGES_EDIT]: 'Edit PowerPages',
  [Permission.POWER_PAGES_DELETE]: 'Delete PowerPages',

  [Permission.ANALYTICS_VIEW]: 'View Analytics',
  [Permission.ANALYTICS_EXPORT]: 'Export Analytics',
  [Permission.REPORTS_VIEW]: 'View Reports',
  [Permission.REPORTS_CREATE]: 'Create Reports',

  [Permission.DOMAIN_VIEW]: 'View Domain',
  [Permission.DOMAIN_EDIT]: 'Edit Domain',
  [Permission.DOMAIN_SETTINGS]: 'Domain Settings',

  [Permission.ACCOUNT_VIEW]: 'View Account',
  [Permission.ACCOUNT_EDIT]: 'Edit Account',
  [Permission.ACCOUNT_MANAGE_DOMAINS]: 'Manage Domains',

  [Permission.SYSTEM_SETTINGS]: 'System Settings',
  [Permission.AUDIT_LOGS_VIEW]: 'View Audit Logs',
  [Permission.E_LINKS_VIEW]: 'View E-Links',
};

